import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import FloatingNav from '../components/FloatingNav';
import BackToHome from '../components/BackToHome';
import { Shield, MessageSquareWarning, Bell, HeartHandshake } from 'lucide-react';
import { ASSETS } from '../config/assets';

const ReviewShieldPage = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: MessageSquareWarning,
      title: t('reviewShield.feature1Title'),
      desc: t('reviewShield.feature1Desc'),
    },
    {
      icon: Bell,
      title: t('reviewShield.feature2Title'),
      desc: t('reviewShield.feature2Desc'),
    },
    {
      icon: HeartHandshake,
      title: t('reviewShield.feature3Title'),
      desc: t('reviewShield.feature3Desc'),
    },
  ];

  return (
    <div data-testid="review-shield-page" className="bg-[#0D0D0D]">
      {/* Floating Navigation */}
      <FloatingNav sections={[]} />
      <BackToHome />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-gradient-to-b from-darkblue/40 to-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400">
                  {t('reviewShield.title')}
                </span>
              </div>
              <h1 
                data-testid="review-shield-title"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white leading-tight mb-6"
              >
                {t('reviewShield.subtitle')}
              </h1>
              <p className="text-lg sm:text-xl text-white/60 leading-relaxed mb-8">
                {t('reviewShield.heroDesc')}
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  data-testid="review-shield-cta"
                  className="btn-demo-cta text-white rounded-lg px-8 py-6"
                >
                  {t('nav.requestDemo')}
                </Button>
              </Link>
            </div>
            <div className="hidden lg:flex justify-start pl-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_tap-platform-build/artifacts/sqdpo1ib_F01D8D85-EBBE-4E01-B8D7-900BCE22C907.png"
                alt="Crab Shack B-Heard"
                className="w-[420px] max-h-[380px] object-cover object-top rounded-3xl shadow-2xl shadow-black/50"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                data-testid={`shield-feature-${idx}`}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:-translate-y-1 hover:border-blue-500/30 transition-all duration-300"
              >
                <div className="p-3 bg-blue-500/10 rounded-xl w-fit mb-6">
                  <feature.icon className="w-6 h-6 text-blue-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-medium text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/50 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Positive Flow — Create Brand Ambassadors */}
      <section className="py-20 lg:py-28 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-testid="shield-positive-image">
              <img
                src={ASSETS.REVIEW_SHIELD_POSITIVE}
                alt="Positive review flow"
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400">Positive</span>
              </div>
              <h2 data-testid="shield-positive-title" className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
                {t('reviewShield.positiveTitle')}
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                {t('reviewShield.positiveDesc')}
              </p>
              <Link to="/contact">
                <Button 
                  data-testid="shield-positive-cta"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-6 py-3"
                >
                  {t('nav.getStarted')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Negative Flow — Private Resolution */}
      <section className="py-20 lg:py-28 bg-[#0D0D0D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-testid="shield-negative-image">
              <img
                src={ASSETS.REVIEW_SHIELD_NEGATIVE}
                alt="Negative review recovery flow"
                className="rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-xs font-semibold uppercase tracking-widest text-blue-400">Recovery</span>
              </div>
              <h2 data-testid="shield-negative-title" className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
                {t('reviewShield.negativeTitle')}
              </h2>
              <p className="text-white/60 text-lg leading-relaxed mb-8">
                {t('reviewShield.negativeDesc')}
              </p>
              <Link to="/contact">
                <Button 
                  data-testid="shield-negative-cta"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-6 py-3"
                >
                  {t('nav.getStarted')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewShieldPage;
